import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge , EstadoBadge } from "../components/Semaforo";
import { PROJECT_ESTADOS, EJES_ESTRATEGICOS } from "../lib/constants";
import { useConfirm } from "../components/ConfirmDialog";

export default function Projects() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  function empty() {
    return {
      name: "", description: "", eje_estrategico: "", tipo: "",
      related_entity_type: params.get("related_entity_type") || "",
      related_entity_id: params.get("related_entity_id") || "",
      fecha_inicio: "", fecha_termino: "", estado: "planificacion",
      responsable: "", responsable_email: "", organizaciones_involucradas: "",
      presupuesto: 0, inversion_ejecutada: 0,
      beneficiarios_directos: 0, beneficiarios_indirectos: 0,
      indicadores_impacto: [],
    };
  }

  const load = async () => {
    const relType = params.get("related_entity_type");
    const relId = params.get("related_entity_id");
    const url = relType && relId ? `/projects?related_entity_type=${relType}&related_entity_id=${relId}` : "/projects";
    const { data } = await api.get(url);
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/communities").then((r) => setCommunities(r.data));
    api.get("/authorities").then((r) => setAuthorities(r.data));
    if (params.get("new") === "1") setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submit = async () => {
    try {
      const p = { ...form };
      ["presupuesto","inversion_ejecutada","beneficiarios_directos","beneficiarios_indirectos"].forEach(k => p[k] = +p[k] || 0);
      p.fecha_inicio = p.fecha_inicio || null;
      p.fecha_termino = p.fecha_termino || null;
      if (!p.responsable_email) p.responsable_email = null;
      if (!p.related_entity_type) { p.related_entity_type = null; p.related_entity_id = null; }
      if (!p.related_entity_id) p.related_entity_id = null;
      await api.post("/projects", p);
      toast.success("Proyecto creado");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Eliminar proyecto", description: "¿Deseas eliminar este proyecto? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/projects/${id}`); load();
  };

  const formatCLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(+n || 0);

  return (
    <div data-testid="projects-page">
      <PageHeader
        title="Proyectos"
        description="Iniciativas con eje estratégico (ODS, ICMM, Copper Mark), inversión, ejecución y beneficiarios."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" className="bg-slate-900 hover:bg-slate-800 text-white"><Plus weight="bold" /> Nuevo proyecto</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nuevo proyecto</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
                </div>
                <div>
                  <Label>Eje estratégico</Label>
                  <Select value={form.eje_estrategico || "__none__"} onValueChange={(v) => setForm({ ...form, eje_estrategico: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {EJES_ESTRATEGICOS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} />
                </div>
                <div>
                  <Label>Comunidad / Autoridad asociada</Label>
                  <Select value={form.related_entity_type || "__none__"} onValueChange={(v) => setForm({ ...form, related_entity_type: v === "__none__" ? "" : v, related_entity_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      <SelectItem value="community">Comunidad</SelectItem>
                      <SelectItem value="authority">Autoridad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Entidad</Label>
                  <Select value={form.related_entity_id || "__none__"} onValueChange={(v) => setForm({ ...form, related_entity_id: v === "__none__" ? "" : v })} disabled={!form.related_entity_type}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {(form.related_entity_type === "authority" ? authorities : communities).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha inicio</Label>
                  <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
                </div>
                <div>
                  <Label>Fecha término</Label>
                  <Input type="date" value={form.fecha_termino} onChange={(e) => setForm({ ...form, fecha_termino: e.target.value })} />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PROJECT_ESTADOS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Responsable</Label>
                  <Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
                </div>
                <div>
                  <Label>Email(s) responsable — separa con coma para varios</Label>
                  <Input type="text" value={form.responsable_email} onChange={(e) => setForm({ ...form, responsable_email: e.target.value })} placeholder="nombre@empresa.cl, otro@empresa.cl" />
                </div>
                <div className="col-span-2">
                  <Label>Organizaciones involucradas</Label>
                  <Input value={form.organizaciones_involucradas} onChange={(e) => setForm({ ...form, organizaciones_involucradas: e.target.value })} />
                </div>
                <div>
                  <Label>Presupuesto (CLP)</Label>
                  <Input type="number" value={form.presupuesto} onChange={(e) => setForm({ ...form, presupuesto: e.target.value })} />
                </div>
                <div>
                  <Label>Inversión ejecutada (CLP)</Label>
                  <Input type="number" value={form.inversion_ejecutada} onChange={(e) => setForm({ ...form, inversion_ejecutada: e.target.value })} />
                </div>
                <div>
                  <Label>Beneficiarios directos</Label>
                  <Input type="number" value={form.beneficiarios_directos} onChange={(e) => setForm({ ...form, beneficiarios_directos: e.target.value })} />
                </div>
                <div>
                  <Label>Beneficiarios indirectos</Label>
                  <Input type="number" value={form.beneficiarios_indirectos} onChange={(e) => setForm({ ...form, beneficiarios_indirectos: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-project-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-6 py-6">
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proyecto</TableHead>
                <TableHead>Eje</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead className="text-right">Presupuesto</TableHead>
                <TableHead className="text-right">Ejecutado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Sin proyectos.</TableCell></TableRow>}
              {items.map(p => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/proyectos/${p.id}`)}>
                  <TableCell className="font-medium">
                    <Link to={`/proyectos/${p.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{p.name}</Link>
                    <div className="text-xs text-slate-500">{p.tipo || ""}</div>
                  </TableCell>
                  <TableCell>{p.eje_estrategico || "—"}</TableCell>
                  <TableCell><EstadoBadge value={p.estado} /></TableCell>
                  <TableCell>{p.responsable || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{formatCLP(p.presupuesto)}</TableCell>
                  <TableCell className="text-right font-mono text-brand-green-700">{formatCLP(p.inversion_ejecutada)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/proyectos/${p.id}`}><Button size="sm" variant="ghost"><ArrowSquareOut size={14} /></Button></Link>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash size={14} className="text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
